import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import { FaPhone, FaEnvelope, FaLinkedin, FaFacebook, FaInstagram, FaArrowRight } from "react-icons/fa";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Team = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await fetch(`${API}/cards`);
        const data = await res.json();
        setCards(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Our Team | Crown Amusement Gaming Professionals</title>
        <meta name="description" content="Meet the Crown Amusement team - passionate professionals dedicated to delivering premium skill gaming solutions, cabinets, fish games, and vending services." />
        <link rel="canonical" href="https://crownamusement.com/team" />
        <meta property="og:title" content="Our Team | Crown Amusement" />
        <meta property="og:description" content="Meet the team behind Crown Amusement's premium skill gaming solutions." />
        <meta property="og:url" content="https://crownamusement.com/team" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-obsidian relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 30px, #D4AF37 30px, #D4AF37 31px)"
        }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20">
              The People Behind the Games
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Meet Our <span className="text-gold-gradient">Team</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-base lg:text-lg">
              Passionate professionals dedicated to delivering premium skill gaming solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-16 bg-obsidian" data-testid="team-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-80 rounded-2xl bg-surface animate-pulse" />
              ))}
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">Team cards coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {cards.map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                  data-testid={`team-card-${card.slug}`}
                >
                  <Link to={`/card/${card.slug}`} className="block group">
                    <div className="relative bg-surface border border-white/5 rounded-2xl overflow-hidden hover:border-gold-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-gold-500/5">
                      {/* Top accent */}
                      <div className="h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />

                      {/* Background pattern */}
                      <div className="h-24 bg-gradient-to-br from-gold-500/5 to-transparent relative">
                        <div className="absolute inset-0 opacity-[0.03]" style={{
                          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, #D4AF37 8px, #D4AF37 9px)"
                        }} />
                      </div>

                      {/* Photo */}
                      <div className="flex justify-center -mt-14 relative z-10">
                        <div className="w-28 h-28 rounded-full border-4 border-surface overflow-hidden bg-charcoal shadow-lg group-hover:border-gold-500/20 transition-colors">
                          {card.photo ? (
                            <img src={card.photo} alt={card.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gold-500 text-2xl font-bold bg-gold-500/5">
                              {card.name.split(" ").map(n => n[0]).join("").toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="text-center px-6 pt-4 pb-2">
                        <h3 className="font-heading text-lg text-white group-hover:text-gold-400 transition-colors">
                          {card.name}
                        </h3>
                        <p className="text-gold-500/80 text-sm mt-0.5">{card.title}</p>
                      </div>

                      {/* Contact preview */}
                      <div className="px-6 pt-2 pb-4 space-y-1.5">
                        {card.phone && (
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                            <FaPhone className="text-gold-500/40" />
                            <span>{card.phone}</span>
                          </div>
                        )}
                        {card.email && (
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 truncate">
                            <FaEnvelope className="text-gold-500/40 flex-shrink-0" />
                            <span className="truncate">{card.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Social icons */}
                      {(card.linkedin || card.facebook || card.instagram) && (
                        <div className="flex justify-center gap-3 pb-4">
                          {card.linkedin && <FaLinkedin className="text-gray-600 text-sm" />}
                          {card.facebook && <FaFacebook className="text-gray-600 text-sm" />}
                          {card.instagram && <FaInstagram className="text-gray-600 text-sm" />}
                        </div>
                      )}

                      {/* View card CTA */}
                      <div className="px-6 pb-5">
                        <div className="flex items-center justify-center gap-2 text-xs text-gold-500/60 group-hover:text-gold-400 transition-colors">
                          <span>View Card</span>
                          <FaArrowRight className="text-[10px] group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-obsidian border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-gray-400 mb-8">
            Our team is here to help you find the perfect gaming solutions for your location.
          </p>
          <Link
            to="/contact"
            data-testid="team-contact-cta"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-semibold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
          >
            Get in Touch <FaArrowRight />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Team;
